import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DiscountSkeleton from './DiscountSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import LocalizedNavLink from '../../../hooks/LocalizedNavLink';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import './Discount.scss';

const Discount = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { 
        data: discountJson, 
        loading: dataLoading, 
        error: dataError, 
        refetch 
    } = useCachedFetch('/data/locales/discount.json');

    const [isLoading, setIsLoading] = useState(true);
    const [hasData, setHasData] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setHasData(false);
            setIsLoading(false);
            return;
        }

        const generalInfo = discountJson?.DiscountData?.GeneralInfo?.[currentLang] || discountJson?.DiscountData?.GeneralInfo?.en;
        const offers = discountJson?.DiscountData?.offers;

        if (generalInfo && 
            generalInfo.discountTitle && 
            generalInfo.discountSubtitle && 
            generalInfo.offersTitle && 
            generalInfo.offersSubtitle && 
            offers && 
            Array.isArray(offers) && 
            offers.length > 0) {
            setHasData(true);
            setHasError(false);
        } else {
            setHasData(false);
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, discountJson, currentLang]);

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const discountData = useMemo(() => 
        discountJson?.DiscountData || null,
        [discountJson]
    );

    const generalInfo = useMemo(() => 
        discountData?.GeneralInfo?.[currentLang] || discountData?.GeneralInfo?.en || {},
        [discountData, currentLang]
    );

    const offers = useMemo(() => 
        discountData?.offers || [],
        [discountData]
    );

    const topOffers = useMemo(() => 
        offers.filter(offer => offer.turnOnTop),
        [offers]
    );

    const bottomOffers = useMemo(() => 
        offers.filter(offer => offer.turnOnBottom),
        [offers]
    );

    const getOfferTranslations = useCallback((offer) => 
        offer.translations?.[currentLang] || offer.translations?.en || {},
        [currentLang]
    );

    const blockStyles = useMemo(() => ({
        discountBg: generalInfo.bgBlockDiscount || '#f2f2f2',
        offerBg: generalInfo.bgBlockOffer || '#f2f2f2'
    }), [generalInfo]);

    const hasValidData = useMemo(() => 
        hasData && discountData && (topOffers.length > 0 || bottomOffers.length > 0),
        [hasData, discountData, topOffers.length, bottomOffers.length]
    );

    if (isLoading) {
        return <DiscountSkeleton />;
    }

    if (hasError) {
        return <ErrorState onRetry={handleRetry} />;
    }

    if (!hasValidData) {
        return null;
    }

    return (
        <div className="discount__wrapper__section">
            {topOffers.length > 0 && (
                <div className='discount__wrapper__section__top'>
                    <div className="discount__wrapper__section__desc">
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                        <h4 className="discount__wrapper__section__desc__title">{generalInfo.discountTitle}</h4>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={100}>
                        <h5 className="discount__wrapper__section__desc__subtitle">{generalInfo.discountSubtitle}</h5>
                        </AnimatedOnScroll>
                    </div>
                    <div className="discount__wrapper__section__offers">
                        {topOffers.map((offer) => {
                            const offerTranslations = getOfferTranslations(offer);
                            return (
                                
                                <div 
                                    key={offer.id} 
                                    style={{ backgroundColor: blockStyles.discountBg }} 
                                    className="discount__offer"
                                >
                                    <div className='sale'>
                                        <AnimatedOnScroll animation="fade-right" delay={0}>
                                        <h6 className='sale__discount'>{offer.discount}</h6>
                                        </AnimatedOnScroll>
                                    </div>
                                    <div className='discount__offer__link__wrapper'>
                                        <AnimatedOnScroll animation="fade-up" delay={0}>
                                        <LocalizedNavLink to={offer.link} className="discount__offer__link">
                                            {offerTranslations.title?.map((line, index) => (
                                                <h3 key={index} className="discount__offer__link__line">{line}</h3>
                                            ))}
                                        </LocalizedNavLink>
                                        </AnimatedOnScroll>
                                        <AnimatedOnScroll animation="fade-up" delay={100}>
                                        <p className="discount__offer__text">{offerTranslations.description}</p>
                                        </AnimatedOnScroll>
                                    </div>
                                    <AnimatedOnScroll animation="fade-up" delay={200}>
                                    <Link
                                        to={`form?promocode=${offerTranslations.promocode}`}
                                        className="discount__offer__button"
                                    >
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        {offerTranslations.button?.text}
                                    </Link>
                                    </AnimatedOnScroll>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {bottomOffers.length > 0 && (
                <div className='offers__wrapper__section__bottom'>
                    <div className="offers__wrapper__section__desc">
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                        <h4 className="offers__wrapper__section__desc__title">{generalInfo.offersTitle}</h4>
                        </AnimatedOnScroll>
                        <AnimatedOnScroll animation="fade-up" delay={100}>
                        <h5 className="offers__wrapper__section__desc__subtitle">{generalInfo.offersSubtitle}</h5>
                        </AnimatedOnScroll>
                    </div>
                    <div className="offers__wrapper__section__offers">
                        {bottomOffers.map((offer) => {
                            const offerTranslations = getOfferTranslations(offer);
                            
                            return (
                                <div 
                                    key={offer.id} 
                                    style={{ backgroundColor: blockStyles.offerBg }} 
                                    className="offers__offer"
                                >
                                    <div className='sale'>
                                        <AnimatedOnScroll animation="fade-right" delay={0}>
                                        <h6 className='sale__offers'>{offer.discount}</h6>
                                        </AnimatedOnScroll>
                                    </div>
                                    <div className='offers__offer__link__wrapper'>
                                        <AnimatedOnScroll animation="fade-up" delay={100}>
                                        <LocalizedNavLink to={offer.link} className="offers__offer__link">
                                            {offerTranslations.title?.map((line, index) => (
                                                <h3 key={index} className="offers__offer__link__line">{line}</h3>
                                            ))}
                                        </LocalizedNavLink>
                                        </AnimatedOnScroll>
                                        <AnimatedOnScroll animation="fade-up" delay={200}>
                                        <p className="offers__offer__text">{offerTranslations.description}</p>
                                        </AnimatedOnScroll>
                                    </div>
                                    <AnimatedOnScroll animation="fade-up" delay={300}>
                                    <Link
                                        to={`form?promocode=${offerTranslations.promocode}`}
                                        className="offers__offer__button"
                                    >
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        {offerTranslations.button?.text}
                                    </Link>
                                    </AnimatedOnScroll>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(Discount);